import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Badge, Button, Card, EmptyState, Tabs, notifyError } from "../../../components/ui";
import { SectionFrame } from "../components/SectionFrame";
import { useAlerts, useRadarMutation } from "../hooks/useRadarCorreo";
import { radarCorreoApi, apiErrorMessage, type AlertStatus } from "../services/radarCorreoService";
import { ALERT_STATUS_TONE, SEVERITY_TONE, fmtDateTime } from "../lib/format";

const FILTERS = ["open", "ack", "snoozed", "resolved", "all"] as const;
const SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 } as const;

export default function AlertasSection() {
  const { t, i18n } = useTranslation("radarCorreo");
  const navigate = useNavigate();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("open");
  const alerts = useAlerts(filter === "all" ? undefined : (filter as AlertStatus));
  const update = useRadarMutation((args: { id: string; status: AlertStatus; snoozeDays?: number }) => radarCorreoApi.updateAlert(args.id, args.status, args.snoozeDays));
  const set = (id: string, status: AlertStatus, snoozeDays?: number) =>
    update.mutate({ id, status, snoozeDays }, { onError: (err) => void notifyError(t("common.error"), apiErrorMessage(err) || undefined) });

  const rows = [...(alerts.data || [])].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] || b.lastAt - a.lastAt);

  return (
    <SectionFrame
      title={t("alerts.title")}
      subtitle={t("alerts.subtitle")}
      loading={alerts.isLoading}
      error={alerts.error}
      onRetry={() => void alerts.refetch()}
      filters={<Tabs items={FILTERS.map((f) => ({ id: f, label: t(`alerts.filters.${f}`) }))} activeId={filter} onChange={(id) => setFilter(id as (typeof FILTERS)[number])} />}
    >
      {rows.length === 0 ? (
        <EmptyState icon="ri-notification-off-line" title={t("alerts.empty")} />
      ) : (
        <div className="space-y-2" data-testid="radar-alerts">
          {rows.map((a) => (
            <Card key={a.id} padding="sm" className="flex flex-wrap items-start gap-3">
              <Badge tone={SEVERITY_TONE[a.severity]} variant="solid">
                {t(`alerts.severity.${a.severity}`)}
              </Badge>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-content">{a.title}</p>
                <p className="text-xs text-content-secondary">{a.description}</p>
                <p className="mt-1 text-[11px] text-content-faint">
                  {fmtDateTime(a.lastAt, i18n.language)}
                  {a.count > 1 ? ` · ${t("alerts.repeated", { count: a.count })}` : ""}
                </p>
              </div>
              <Badge tone={ALERT_STATUS_TONE[a.status]} variant="outline">
                {t(`alerts.filters.${a.status}`)}
              </Badge>
              <div className="flex w-full flex-wrap gap-1.5 sm:w-auto">
                {a.operationId && (
                  <Button size="xs" variant="secondary" onClick={() => navigate(`/radar-correo/operaciones?id=${encodeURIComponent(a.operationId as string)}`)}>
                    {t("alerts.actions.open")}
                  </Button>
                )}
                {a.status === "open" && (
                  <Button size="xs" variant="ghost" onClick={() => set(a.id, "ack")}>
                    {t("alerts.actions.ack")}
                  </Button>
                )}
                {a.status !== "resolved" && (
                  <>
                    <Button size="xs" variant="ghost" onClick={() => set(a.id, "snoozed", 3)}>
                      {t("alerts.actions.snooze")}
                    </Button>
                    <Button size="xs" variant="successSoft" onClick={() => set(a.id, "resolved")}>
                      {t("alerts.actions.resolve")}
                    </Button>
                  </>
                )}
                {a.status === "resolved" && (
                  <Button size="xs" variant="ghost" onClick={() => set(a.id, "open")}>
                    {t("alerts.actions.reopen")}
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </SectionFrame>
  );
}
